// src/contexts/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from "react";
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebaseClient";
import { setUser as setErrorTrackingUser, captureException } from "../lib/errorTracking";
import { logWarn, logError } from "../lib/logger";

const AuthContext = createContext({});

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);

  // User roles
  const ROLES = {
    ADMIN: "admin",
    BUYER: "buyer",
    STORE_MANAGER: "store_manager",
  };

  // Permissions check
  const hasPermission = (requiredRole) => {
    if (!userRole) {
      return false;
    }

    const roleHierarchy = {
      [ROLES.ADMIN]: [ROLES.ADMIN, ROLES.BUYER, ROLES.STORE_MANAGER],
      [ROLES.BUYER]: [ROLES.BUYER],
      [ROLES.STORE_MANAGER]: [ROLES.STORE_MANAGER],
    };

    return roleHierarchy[userRole]?.includes(requiredRole) || false;
  };

  // Fetch user role from Firestore
  const fetchUserRole = async (user) => {
    if (!user) {
      setUserRole(null);
      return;
    }

    try {
      const userDoc = await getDoc(doc(db, "users", user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setUserRole(userData.role || null);
      } else {
        // User document doesn't exist - do NOT auto-assign admin role
        // User must go through proper admin setup flow
        setUserRole(null);
        logWarn("User document not found. User needs to be assigned a role by an admin.");
      }
    } catch (error) {
      logError("Error fetching user role", error);
      captureException(error, {
        tags: { source: "auth", action: "fetchUserRole" },
        extra: { userId: user.uid },
      });
      setUserRole(null);
    }
  };

  // Sign up - creates user but does NOT assign role
  // Role must be assigned by an existing admin or through setup flow
  async function signup(email, password) {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    // Do NOT automatically assign admin role - security risk
    // User must be assigned a role by an admin or through secure setup flow
    return userCredential;
  }

  // Sign in
  async function login(email, password) {
    return await signInWithEmailAndPassword(auth, email, password);
  }

  // Sign out
  async function logout() {
    setUserRole(null);
    return await signOut(auth);
  }

  // Password reset
  async function resetPassword(email) {
    return await sendPasswordResetEmail(auth, email);
  }

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      // Update error tracking with user context
      if (user) {
        setErrorTrackingUser({
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
        });
        await fetchUserRole(user);
      } else {
        setErrorTrackingUser(null);
        setUserRole(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userRole,
    login,
    signup,
    logout,
    resetPassword,
    hasPermission,
    ROLES,
    loading,
  };

  return <AuthContext.Provider value={value}>{!loading && children}</AuthContext.Provider>;
}
